/** @typedef { import("src/types").SelectedLocation } SelectedLocation */
/** @typedef {"button" | "link"} LocationButtonChildType*/
import { cacheLocation } from "../../services/state";
import { effect } from "@preact/signals";

/**
 * Either assigns or directs user to a location. See examples for expected HTML compositon
 *
 * @example
 * // If the user is on the restaurant page
 * <mx-location-button id="123" slug="city-location" title="City Location">
 *   <a href="/restaurants/city-location">
 *     <span>Your Location: City Location</span>
 *     <img src="icon.svg"/>
 *    </a>
 * </mx-location-button>
 *
 * // If the user is on another page and their selected location is visible
 * <mx-location-button id="123" slug="city-location" title="City Location">
 *   <a href="/restaurants/city-location">
 *     <span>See Restaurant</span>
 *     <img src="icon.svg"/>
 *    </a>
 * </mx-location-button>
 *
 * // For any other location wrap a button
 * <mx-location-button id="123" slug="city-location" title="City Location">
 *   <button>
 *     <span>Set Restaurant</span>
 *     <img src="icon.svg"/>
 *    </button>
 * </mx-location-button>
 */
class LocationButton extends HTMLElement {
  static observedAttributes = ["id"];

  /** @type {LocationButtonChildType} */
  buttonType = null;

  constructor() {
    super();
    this.renderLink = this.renderLink.bind(this);
    this.renderButton = this.renderButton.bind(this);
    this.subscribe = this.subscribe.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this);
    switch (this.firstElementChild.localName) {
      case "a":
        this.buttonType = "link";
        break;
      case "button":
        this.buttonType = "button";
        break;
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this);
  }

  /**
   * @param {Event & { target: HTMLButtonElement }} event
   */
  handleEvent(event) {
    const id = this.getAttribute("id");
    const slug = this.getAttribute("slug");
    const title = this.getAttribute("title");
    const linkEl = this.querySelector("a");
    const currentPage = new URL(location.href);

    // Don't reload the page if they're currently on it
    if (this.buttonType === "link" && linkEl) {
      const link = new URL(linkEl.href);
      if (currentPage.pathname === link.pathname) {
        event.preventDefault();
        return;
      }
    }

    if (event.target.localName === "button" || event.target.closest("button")) {
      /** @type SelectedLocation */
      const restaurant = {
        id: Number(id),
        slug,
        title,
      };
      this.state.selectedLocation.value = restaurant;

      cacheLocation(restaurant);
      const redirectUrl = new URL(`/restaurants/${slug}`, location.href);
      if (currentPage.pathname !== redirectUrl.pathname) {
        location.assign(redirectUrl);
      }
    }
  }

  /**
   * @param {ReturnType<import("src/services/state").createAppState>} state
   * @example
   * const state = signal(42);
   * const el = document.querySelector('mx-location-button');
   * el.subscribe(state)
   */
  subscribe(state) {
    if (!this.state) {
      this.state = state;
      this.subscriber = effect(() => {
        const nextState = state.selectedLocation.value;
        if (this.buttonType === "link") {
          this.renderLink(nextState);
        } else if (this.buttonType === "button") {
          this.renderButton(nextState);
        }
      });
    }
  }

  /**
   * If a user selects this location, convert any buttons to link
   * @param {SelectedLocation} [selected]
   */
  renderLink(selected) {
    const link = this.querySelector("a");
    const id = this.getAttribute("id");

    if (!selected?.id && Number(id) !== selected?.id) {
      return;
    }

    if (link && selected) {
      const textContainer = link.querySelector("span");
      const hasPrependedText = link.textContent
        .trim()
        .toLowerCase()
        .startsWith("your location:");
      let linkText = "";

      if (hasPrependedText) {
        linkText = "Your Location: ";
      }

      link.setAttribute("id", id);
      link.setAttribute("href", `/restaurants/${selected.slug}`);

      if (link.textContent.trim() !== "Your Location") {
        linkText += selected.title;
        textContainer.textContent = linkText;
      }
    }
  }

  /**
   * If a user selects another location, convert link to a button
   * @param {SelectedLocation} [selected]
   */
  renderButton(selected) {
    const id = this.getAttribute("id");
    const slug = this.getAttribute("slug");
    const buttonText =
      this.getAttribute("data-button-text") ?? "Make this your location";
    const isCurrent = selected?.id === Number(id);
    const textContent = isCurrent ? "Your Location" : buttonText;
    const iconEl = this.querySelector("img");

    this.removeChild(this.firstElementChild);

    if (isCurrent) {
      const linkEl = document.createElement("a");
      const spanEl = document.createElement("span");
      linkEl.setAttribute("href", `/restaurants/${slug}`);
      spanEl.textContent = textContent;
      linkEl.appendChild(spanEl);
      if (iconEl) {
        linkEl.appendChild(iconEl);
      }
      this.prepend(linkEl);
    } else {
      const buttonEl = document.createElement("button");
      const spanEl = document.createElement("span");
      spanEl.textContent = textContent;
      buttonEl.appendChild(spanEl);
      if (iconEl) {
        buttonEl.appendChild(iconEl);
      }
      this.prepend(buttonEl);
    }
  }
}

export default LocationButton;
